import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_SERVICE } from "../Services/CommonApi";
import { commonPayloadFields } from "../helper/storage";
import {
  GET_DEPARTMENT_CAPABILITY,
  GET_DEPARTMENT_APPROVALS,
  ADD_EDIT_DEPARTMENT_APPROVAL,
  DELETE_DEPARTMENT_APPROVAL,
  GET_DEPARTMENT_APPROVAL_CHAIN,
  GET_DOCUMENT_APPROVAL_CHAIN,
} from "../constant/CommonApiUrl";

// Department routing API. Matrix CRUD is deliberately ungated —
// configuration precedes enablement (see GetDepartmentCapability).

// messageCode -> human copy. ROLE_HAS_NO_USERS is a success (row saved),
// not an error — callers branch on it separately.
const DEPARTMENT_ROUTING_MESSAGES = {
  INVALID_APPROVER: "Name exactly one approver — a user or a role, not both or neither.",
  INVALID_LEVEL: "Approval level must be 1 or higher.",
  INVALID_DEPARTMENT: "Selected department no longer exists — refresh and try again.",
  INVALID_USER: "Selected user no longer exists — refresh and try again.",
  DUPLICATE: "That approver is already configured at this level, department and vessel.",
  NOT_FOUND: "That row no longer exists — it may already have been removed.",
};

export const getDepartmentRoutingMessage = (messageCode, fallbackMessage) =>
  DEPARTMENT_ROUTING_MESSAGES[messageCode] || fallbackMessage || "Something went wrong.";

const FETCH_CAPABILITY = "departmentRouting/fetchCapability";
const FETCH_APPROVALS = "departmentRouting/fetchApprovals";
const SAVE_APPROVAL = "departmentRouting/saveApproval";
const REMOVE_APPROVAL = "departmentRouting/removeApproval";
const FETCH_CHAIN_PREVIEW = "departmentRouting/fetchChainPreview";
const FETCH_DOCUMENT_CHAIN = "departmentRouting/fetchDocumentChain";

export const fetchDepartmentCapability = createAsyncThunk(
  FETCH_CAPABILITY,
  async (params = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_DEPARTMENT_CAPABILITY, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const fetchDepartmentApprovals = createAsyncThunk(
  FETCH_APPROVALS,
  async (params = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_DEPARTMENT_APPROVALS, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveDepartmentApproval = createAsyncThunk(
  SAVE_APPROVAL,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(ADD_EDIT_DEPARTMENT_APPROVAL, {
        id: payload.id || 0,
        ...payload,
        createdBy: commonPayloadFields.createdby,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const removeDepartmentApproval = createAsyncThunk(
  REMOVE_APPROVAL,
  async ({ id, include_inactive = false }, thunkAPI) => {
    try {
      // A soft-deactivated rule is otherwise unreachable to this lookup --
      // pass include_inactive: true when deleting an already-inactive row.
      const response = await API_SERVICE.del(DELETE_DEPARTMENT_APPROVAL, {
        id,
        include_inactive,
        updatedby: commonPayloadFields.updatedby,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Pre-submit preview — used by PR creation (later phase). Not gated.
export const fetchDepartmentApprovalChainPreview = createAsyncThunk(
  FETCH_CHAIN_PREVIEW,
  async (params = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_DEPARTMENT_APPROVAL_CHAIN, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Chain of record on a saved document — used by PR view/approve (later
// phase). document_type spans PURCHASE_REQUISITION/RFQ/QUOTATION/etc., so
// this lives here rather than in a document-specific slice.
export const fetchDocumentApprovalChain = createAsyncThunk(
  FETCH_DOCUMENT_CHAIN,
  async (params, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_DOCUMENT_APPROVAL_CHAIN, params);
      return { key: `${params.document_type}:${params.document_id}`, ...response.data.result };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

const initialState = {
  capability: null, // { department_routing, department_visibility, department_approval, department_strict, exempt_roles, department_id, department_name, resolved_via }
  capabilityStatus: "idle", // idle | loading | loaded | error
  capabilityVesselId: null,

  list: [], // department x level matrix rows
  totalData: 0,
  matrixStatus: "idle", // idle | loading | loaded | error

  preview: null,
  previewMessageCode: null,
  previewStatus: "idle",

  documentChains: {}, // "DOCUMENT_TYPE:id" -> chain-of-record response

  lastError: null, // { messageCode, message } from the most recent write/delete
};

const departmentRoutingSlice = createSlice({
  name: "departmentRouting",
  initialState,
  reducers: {
    clearDepartmentRoutingError: (state) => {
      state.lastError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartmentCapability.pending, (state) => {
        state.capability = null;
        state.capabilityStatus = "loading";
      })
      .addCase(fetchDepartmentCapability.fulfilled, (state, action) => {
        state.capability = action.payload.data;
        state.capabilityVesselId = action.meta.arg?.vessel_id ?? null;
        state.capabilityStatus = "loaded";
      })
      .addCase(fetchDepartmentCapability.rejected, (state) => {
        state.capability = null;
        state.capabilityVesselId = null;
        state.capabilityStatus = "error";
      })

      .addCase(fetchDepartmentApprovals.pending, (state) => {
        state.matrixStatus = "loading";
      })
      .addCase(fetchDepartmentApprovals.fulfilled, (state, action) => {
        state.list = action.payload.data || [];
        state.totalData = action.payload.totalData || 0;
        state.matrixStatus = "loaded";
      })
      .addCase(fetchDepartmentApprovals.rejected, (state) => {
        state.matrixStatus = "error";
      })

      .addCase(saveDepartmentApproval.fulfilled, (state, action) => {
        state.lastError = action.payload.status
          ? null
          : { messageCode: action.payload.messageCode, message: action.payload.message };
      })

      .addCase(removeDepartmentApproval.fulfilled, (state, action) => {
        state.lastError = action.payload.status
          ? null
          : { messageCode: action.payload.messageCode, message: action.payload.message };
      })

      .addCase(fetchDepartmentApprovalChainPreview.pending, (state) => {
        state.previewStatus = "loading";
      })
      .addCase(fetchDepartmentApprovalChainPreview.fulfilled, (state, action) => {
        // UNMAPPED_DEPARTMENT / CHAIN_INCOMPLETE arrive with status:true —
        // not errors, just different rendering. NO_DEPARTMENT is status:false.
        state.preview = action.payload.data || null;
        state.previewMessageCode = action.payload.messageCode;
        state.previewStatus = "loaded";
      })
      .addCase(fetchDepartmentApprovalChainPreview.rejected, (state) => {
        state.previewStatus = "error";
      })

      .addCase(fetchDocumentApprovalChain.fulfilled, (state, action) => {
        const { key, ...chain } = action.payload;
        // NO_CHAIN is status:true, has_chain:false — a normal, common state
        // (document raised before the matrix was seeded), not an error.
        state.documentChains[key] = chain;
      });
  },
});

export const { clearDepartmentRoutingError } = departmentRoutingSlice.actions;
export default departmentRoutingSlice.reducer;
